// PageDescription: 대면진료 병원 검색 페이지
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import Head from 'next/head';
import React from 'react';

import DefaultLayout from '@/components/layouts/DefaultLayout';
import HospitalsSearchTemplate from '@/components/templates/f2f/hospital/hospitals-search-template';
import useSearchHospitalsGenerallyInfinityQuery from '@/hooks/hospital/list/useSearchHospitalsGenerallyInfinityQuery';
import useHospitalsSearchViewModel from '@/hooks/hospital/search/useHospitalsSearchViewModel';
import { CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';
import { HospitalSearchQueries, SearchGenerallyHospitalVo } from '@/types/hospital';
import { createSearchHospitalLdJson } from '@/utils/format';
import { validateNonSpecialPattern } from '@/utils/validate';

interface Props extends CustomPageProps {
  queries: HospitalSearchQueries;
}

const SearchHospitalSeo = ({
  queries,
  hospitals,
}: Pick<Props, 'queries'> & { hospitals: SearchGenerallyHospitalVo[] }) => {
  const hospitalsJson = createSearchHospitalLdJson(hospitals, queries.keyword);

  const totalCount = hospitals[0]?.hospitalTotalCount ?? 0;

  const title = queries.keyword ? `${queries.keyword} 인기 병원 TOP10 - 닥터나우` : '인기 병원 TOP10 - 닥터나우';

  const description = queries.keyword
    ? `인기 병원을 바로 예약할 수 있어요. TOP10을 포함한 ${queries.keyword} ${totalCount}개 중 나에게 꼭 맞는 병원을 선택해 보세요!`
    : '인기 병원을 바로 예약할 수 있어요. TOP10을 포함한 76,614개 중 나에게 꼭 맞는 병원을 선택해 보세요!';

  const canonical = queries.keyword
    ? `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.HOSPITAL.SEARCH.path}?keyword=${queries.keyword}`
    : `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.HOSPITAL.SEARCH.path}`;
  return (
    <>
      <NextSeo
        title={title}
        description={description}
        canonical={canonical}
        openGraph={{
          title,
          description: '병원 위치부터 후기까지 확인 후, 예약해 보세요!',
          url: canonical,
          images: [
            {
              url: `${process.env.NEXT_PUBLIC_HOSPITAL_SEARCH_IMAGE}`,
              alt: 'Og Image Alt',
            },
          ],
        }}
      />
      <Head>
        <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(hospitalsJson) }} />
      </Head>
    </>
  );
};

const SearchHospitalsPage = ({ client, queries }: Props) => {
  const viewModel = useHospitalsSearchViewModel(client);
  return (
    <>
      <SearchHospitalSeo queries={queries} hospitals={viewModel.hospitals} />
      <HospitalsSearchTemplate client={client} viewModel={viewModel} />
    </>
  );
};

export default SearchHospitalsPage;

SearchHospitalsPage.Layout = DefaultLayout;

export const getServerSideProps: GetServerSideProps<{
  queries: HospitalSearchQueries;
}> = async (ctx) => {
  const queryClient = new QueryClient();

  const queries = ctx.query as HospitalSearchQueries;

  const keyword = queries.keyword ?? '';

  if (!validateNonSpecialPattern(keyword)) {
    return {
      redirect: {
        destination: ROUTES.HOSPITAL.SEARCH.path,
        permanent: true,
      },
    };
  }

  if (queries.keyword) {
    await useSearchHospitalsGenerallyInfinityQuery.prefetchQuery(queryClient, {
      ...queries,
      keyword: queries.keyword,
    });
  }

  return {
    props: {
      queries,
      dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
    },
  };
};
